import { CommonModule } from "@angular/common";
import {
    afterNextRender,
    Component,
    computed,
    inject,
    model,
    OnDestroy,
    signal,
} from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { Router, RouterOutlet } from "@angular/router";
import {
    AbnormalPaymentService,
    CenterUserService,
    ExcelService,
} from "@malirang/api-client";
import { CustomSelectComponent } from "@malirang/app-share";
import {
    AlertService,
    Icon,
    Label,
    MonthPickerComponent,
    Segment,
    SegmentOption,
} from "@malirang/front-share";
import dayjs from "dayjs";
import { UploadService } from "../../../../../services/upload.service";
import { CenterStore } from "../../../../../stores/center/center.store";
import { AbnormalPaymentStore } from "./abnormal-payment.store";
import { AbnormalPaymentFileUploaderComponent } from "./components/abnormal-payment-file-uploader/abnormal-payment-file-uploader.component";
import { of } from "rxjs";
import { injectNavigationEnd } from "ngxtension/navigation-end";

/**
 * 이상 결제
 */
@Component({
    selector: "app-abnormal-payment",
    templateUrl: "./abnormal-payment.layout.html",
    styleUrls: ["./abnormal-payment.layout.css"],
    imports: [
        CommonModule,
        MonthPickerComponent,
        CustomSelectComponent,
        AbnormalPaymentFileUploaderComponent,
        Label,
        Icon,
        Segment,
        RouterOutlet,
    ],
})
export default class AbnormalPaymentLayout implements OnDestroy {
    readonly centerUserService = inject(CenterUserService);
    readonly centerStore = inject(CenterStore);
    readonly uploadService = inject(UploadService);
    readonly router = inject(Router);
    readonly excelService = inject(ExcelService);
    readonly alertService = inject(AlertService);
    readonly store = inject(AbnormalPaymentStore);
    readonly service = inject(AbnormalPaymentService);

    segment = signal<"이상 결제" | "매칭 실패" | "사용량 조회">("이상 결제");
    segments = computed<SegmentOption[]>(() => [
        {
            label: "이상 결제",
            value: "이상 결제",
            count: this.count()?.abnormal || 0,
        },
        {
            label: "매칭 실패",
            value: "매칭 실패",
            count: this.count()?.matchingFailed || 0,
        },
        { label: "사용량 조회", value: "사용량 조회" },
    ]);

    teachers = this.store.teachers;
    status = this.store.status;

    teacherIds = model<string[]>([]);
    startMonth = model<string>();
    endMonth = model<string>();
    uploadFile = model<File>();

    $count = rxResource({
        stream: ({ params }) => {
            if (
                !params.teacherIds ||
                !params.startMonth ||
                !params.endMonth ||
                !params.file
            ) {
                return of(null);
            }

            return this.service.abnormalPaymentControllerGetCount({
                teacherIds: params.teacherIds,
                body: {
                    file: params.file,
                },
                endMonth: params.endMonth,
                startMonth: params.startMonth,
            });
        },
        params: () => ({
            teacherIds: this.teacherIds() || [],
            startMonth: this.startMonth() || "",
            endMonth: this.endMonth() || "",
            file: this.uploadFile()!,
        }),
    });
    count = computed(() =>
        this.$count.hasValue() ? this.$count.value() : null,
    );

    navigationEnd$ = injectNavigationEnd();

    constructor() {
        afterNextRender(() => {
            if (this.router.url.includes("failed-matching")) {
                this.segment.set("매칭 실패");
            } else if (this.router.url.includes("usage")) {
                this.segment.set("사용량 조회");
            } else {
                this.segment.set("이상 결제");
            }
        });
    }

    ngOnDestroy(): void {
        this.reset();
    }

    reset() {
        this.uploadFile.set(undefined);
        this.teacherIds.set([]);
        this.startMonth.set("");
        this.endMonth.set("");
        this.store.setStatus("none");
        this.store.setAbnormalPaymentData(null);
    }

    handleUpload(file: File) {
        if (
            this.teacherIds().length === 0 ||
            !this.startMonth() ||
            !this.endMonth()
        ) {
            this.alertService.warning(
                "필수 항목이 모두 입력되었는지 확인해주세요.",
                "알림",
            );
            return;
        }

        if (!file) {
            return;
        }

        this.store.setStatus("loading");
        this.store.setAbnormalPaymentData({
            endMonth: this.endMonth()!,
            startMonth: this.startMonth()!,
            file,
            teacherIds: this.teacherIds(),
        });
    }

    monthChange() {
        if (this.startMonth() && this.endMonth()) {
            const start = dayjs(this.startMonth()!);
            const end = dayjs(this.endMonth()!);
            if (start.isAfter(end)) {
                const endMonth = this.endMonth();
                this.endMonth.set(this.startMonth());
                this.startMonth.set(endMonth);
            }
        }
    }

    segmentChange() {
        switch (this.segment()) {
            case "이상 결제":
                this.router.navigate(["receipt/abnormal-payment/list"]);
                break;
            case "매칭 실패":
                this.router.navigate([
                    "receipt/abnormal-payment/failed-matching",
                ]);
                break;
            case "사용량 조회":
                this.router.navigate(["receipt/abnormal-payment/usage"]);
                break;
        }
    }
}
